<template>
    <div class="EvaluatedEntityMappingReview">
        <div class="EvaluatedEntityMappingReview__container">
            <div class="EvaluatedEntityMappingReview__form">
                <Table class="EvaluatedEntityMappingReview__table">
                    <template #head>
                        <HeadCell class="EvaluatedEntityMappingReview__requiredCollateralHeader">
                            Required Collateral
                        </HeadCell>
                        <HeadCell class="EvaluatedEntityMappingReview__providedCollateralHeader">
                            Provided Collateral
                        </HeadCell>
                        <HeadCell class="EvaluatedEntityMappingReview__noMatchHeader" />
                    </template>
                    <Row hoverable>
                        <Cell class="EvaluatedEntityMappingReview__requiredCollateral">
                            {{ requiredEntity.description }}
                        </Cell>
                        <Cell class="EvaluatedEntityMappingReview__providedCollateral">
                            <!-- A decided action shows the mapping that was chosen, not the picker. -->
                            <span v-if="readOnly" class="EvaluatedEntityMappingReview__recordedSelection">
                                {{ recordedSelection }}
                            </span>
                            <Dropdown
                                v-else
                                :placeholder="dropdownPlaceholder"
                                :options="options"
                                :selected="selectedOptions"
                                :disabled="noMatch"
                                @input="onCollateralSelection"
                            />
                        </Cell>
                        <Cell class="EvaluatedEntityMappingReview__noMatch">
                            <Checkbox
                                v-if="!readOnly"
                                class="EvaluatedEntityMappingReview__noMatch"
                                @update:model-value="toggleNoMatch"
                            >
                                No Match
                            </Checkbox>
                        </Cell>
                    </Row>
                </Table>
                <RbacDisableDefaultGuard v-if="!readOnly" :features="[RbacFeature.actionsResolveEntityAction]">
                    <div class="flex justify-end gap-3 p-4">
                        <NewButton
                            class="EvaluatedEntityMappingReview__saveButton float-right"
                            variant="outline"
                            color="primary"
                            :disabled="saveDisabled"
                            @click="onSave"
                        >
                            Save
                        </NewButton>
                    </div>
                </RbacDisableDefaultGuard>
            </div>

            <RbacDisplayGuard :features="[RbacFeature.actionsViewDocument]">
                <div class="EvaluatedEntityMappingReview__documents">
                    <LogoLoader v-if="loading" />
                    <DocumentsViewer v-if="!loading" :documents="docsStatus.list" />
                </div>
            </RbacDisplayGuard>
        </div>
    </div>
</template>

<script lang="ts">
    import { Component, Prop, Vue, Watch } from '@evidentid/vue-property-decorator';

    import { Checkbox } from '@evidentid/dashboard-commons/components/Checkbox';
    import { Dropdown } from '@evidentid/dashboard-commons/components/Dropdown';
    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import { LogoLoader } from '@evidentid/dashboard-commons/components/LogoLoader';
    import { Cell, HeadCell, Row, Table } from '@evidentid/dashboard-commons/components/Table';
    import RbacDisableDefaultGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisableDefaultGuard.vue';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { typeNarrowing } from '@evidentid/dashboard-commons/utils/typeNarrowing';
    import {
        ActionReview,
        ActionType,
        EvaluatedCollateralEntity,
        EvaluatedEntity,
        EvaluatedEntityMappingActionDetails,
        ProvidedEntityDetails,
    } from '@evidentid/tprm-portal-lib/models/entity-actions-review';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import BinaryDataDisplay from '@/components/binary-data-display/BinaryDataDisplay.vue';
    import NewButton from '@/components/new-button/NewButton.vue';
    import DocumentsViewer from '@/modules/entity-actions-review/components/DocumentsViewer/DocumentsViewer.vue';
    import EmptyValueDashSymbol
        from '@/modules/entity-actions-review/components/EmptyValueDashSymbol/EmptyValueDashSymbol.vue';
    import { WRONG_ACTION_DETAILS_TYPE_MESSAGE } from '@/modules/entity-actions-review/constants';
    import { CoiDocumentStatus, useEntityActionsReviewStore } from '@/modules/entity-actions-review/store';

    @Component({
        emits: [ 'resolve' ],
        components: {
            RbacDisplayGuard,
            RbacDisableDefaultGuard,
            EmptyValueDashSymbol,
            NewButton,
            BinaryDataDisplay,
            Checkbox,
            Dropdown,
            DocumentsViewer,
            HeadCell,
            LogoLoader,
            Table,
            Cell,
            Row,
        },
    })
    export default class EvaluatedEntityMappingReview extends Vue {
        @Prop({ type: Boolean, default: false })
        private readOnly!: boolean;

        /** The collateral mapping chosen when this action was decided.
         *
         * `providedEntity` is the key the resolve payload actually carries, and it holds the whole entity
         * rather than an id, so it is labelled the same way the dropdown labels its options. Distinguishes
         * three cases the reviewer must not see conflated: a mapping was chosen, no match was chosen, and
         * nothing was ever recorded (actions decided before resolutions were stored).
         */
        private get recordedSelection(): string {
            const resolution = this.actionReview.resolution;
            if (!resolution || resolution.result == null) {
                return 'Not recorded';
            }
            const chosen = (resolution.result as { providedEntity?: EvaluatedCollateralEntity }).providedEntity;
            return chosen ? this.describeCollateral(chosen) : 'No Match';
        }

        private describeCollateral(entity: EvaluatedCollateralEntity): string {
            const separator = entity.description && entity.uniqueIdentifier ? ' - ' : '';
            return !entity.description && !entity.uniqueIdentifier
                ? 'No description/identifier provided'
                : `${entity.description || ''}${separator}${entity.uniqueIdentifier || ''}`;
        }

        @Prop({ type: Object, default: () => ({}) })
        private actionReview!: ActionReview;

        private RbacFeature = RbacFeature;
        private noMatch: boolean = false;
        private selectedOptions: DropdownOption<EvaluatedCollateralEntity>[] = [];
        private options: DropdownOption<EvaluatedCollateralEntity>[] = [];

        private get actionDetails(): EvaluatedEntityMappingActionDetails {
            return typeNarrowing(
                this.actionReview.action,
                this.actionReview.action.$action === ActionType.evaluatedEntityMappingV1,
                WRONG_ACTION_DETAILS_TYPE_MESSAGE,
            );
        }

        private get rpName(): string {
            return this.$rp.current!;
        }

        private get docsStatus(): CoiDocumentStatus {
            if (useEntityActionsReviewStore().rprDocuments[this.rpName] &&
                useEntityActionsReviewStore().rprDocuments[this.rpName][this.actionReview.id]) {
                return useEntityActionsReviewStore().rprDocuments[this.rpName][this.actionReview.id];
            } else {
                return { status: OperationStatus.uninitialized, list: [] };
            }
        }

        private get loading(): boolean {
            return this.docsStatus.status === OperationStatus.loading;
        }

        private get saveDisabled(): boolean {
            return this.selectedOptions.length === 0 && !this.noMatch;
        }

        private get dropdownPlaceholder(): string {
            return this.noMatch ? 'N/A' : 'Choose Mapping';
        }

        private get requiredEntity(): EvaluatedEntity {
            return this.actionDetails.requiredEntity;
        }

        private get providedEntityDetails(): ProvidedEntityDetails[] {
            return this.actionDetails.providedEntities;
        }

        @Watch('actionReview', { immediate: true })
        private onActionReviewChange(): void {
            // [entityMappingExpansion]: only collateral entity atm, get different options per type when expanded
            this.options = this.constructProvidedCollateralOptions();
            if (this.docsStatus.status === OperationStatus.uninitialized) {
                const documentsFor = this.providedEntityDetails.map((x) => ({
                    requirementType: x.coverageType,
                    verificationId: x.verificationId,
                }));
                const uniqueDocumentsFor = [ ...new Set(documentsFor) ];
                useEntityActionsReviewStore().loadRprDocuments({
                    rpName: this.rpName,
                    actionId: this.actionReview.id,
                    documentsFor: uniqueDocumentsFor,
                });
            }
        }

        private constructProvidedCollateralOptions(): DropdownOption<EvaluatedCollateralEntity>[] {
            return this.providedEntityDetails.map((details) => {
                const entity = (details.providedEntity as EvaluatedCollateralEntity);
                const notProvided = 'No description/identifier provided';
                const separator = entity.description && entity.uniqueIdentifier ? ' - ' : '';
                const label = !entity.description && !entity.uniqueIdentifier
                    ? notProvided
                    : `${entity.description || ''}${separator}${entity.uniqueIdentifier || ''}`;
                return {
                    value: entity,
                    label,
                };
            });
        }

        private toggleNoMatch(checked: boolean): void {
            this.noMatch = checked;
            if (checked) {
                this.selectedOptions = [];
            }
        }

        private onCollateralSelection(option: DropdownOption): void {
            this.selectedOptions = [ option ];
        }

        private onSave(): void {
            const mappingResolution = {
                $action: ActionType.evaluatedEntityMappingV1,
                accepted: this.selectedOptions.length > 0 && !this.noMatch,
                ...(this.selectedOptions[0] && { providedEntity: this.selectedOptions[0].value }),
            };
            this.$emit('resolve', mappingResolution);
        }
    }
</script>
